/*
 * THYROID ULTRASOUND worksheet: ACR TI-RADS nodules + cervical lymph nodes.
 *   <TiradsFormSection>  editing, inside the sonographer's form (thyroidUltraSound.jsx)
 *   <TiradsReport>       read-only, on the preview page that v2 generate-pdf
 *                        screenshots for Athena (views/thyroidultrasound.jsx)
 * Arithmetic and stored shape: ./thyroidTirads.js.
 * Choice buttons instead of small selects/checkboxes so they work on a tablet.
 */
import React from "react";
import { FaPlus } from "react-icons/fa";
import {
  COMPOSITION,
  ECHOGENICITY,
  SHAPE,
  MARGIN,
  FOCI,
  VASCULARITY,
  LOBES,
  POLES,
  LN_SIDES,
  LN_LEVELS,
  LN_FEATURES,
  computeTirads,
  emptyNodule,
  emptyLymphNode,
  toggleFocus,
  toggleValue,
  labelOf,
  reportLabelOf,
  noduleLocation,
  spongiformConflicts,
  hasEntries,
  LN_STATUSES,
} from "./thyroidTirads";

const inputCls = "border rounded px-2 py-1 w-full";

function Choice({ selected, onClick, children, testid }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      data-testid={testid}
      onClick={onClick}
      className={`min-h-[40px] px-3 py-1.5 text-sm text-left border rounded ${
        selected ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-gray-300 hover:bg-gray-50"
      }`}
    >
      {children}
    </button>
  );
}

function ChoiceRow({ label, options, value, onPick, multi, showPoints = true, testid }) {
  const isOn = (v) => (multi ? (value || []).includes(v) : value === v);
  return (
    <div className="mt-3" role="group" aria-label={label}>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <Choice key={o.value} selected={isOn(o.value)} onClick={() => onPick(o.value)} testid={testid && `${testid}-${o.value}`}>
            {o.label}
            {showPoints && o.points !== undefined && (
              <span className={`ml-1 text-xs ${isOn(o.value) ? "text-blue-100" : "text-gray-500"}`}>({o.points})</span>
            )}
          </Choice>
        ))}
      </div>
    </div>
  );
}

const fmtPts = (p) => (p === null || p === undefined ? "–" : p);

// Read-only result strip under each nodule (the sonographer cannot edit it).
function TiradsResult({ result, idx }) {
  const p = result.points;
  return (
    <div className="mt-3 rounded border border-gray-300 bg-gray-50 px-3 py-2 text-sm" data-testid={`tirads-result-${idx}`} aria-live="polite">
      <div className="text-gray-700">
        Points: composition {fmtPts(p.composition)} + echogenicity {fmtPts(p.echogenicity)} + shape {fmtPts(p.shape)} + margin{" "}
        {fmtPts(p.margin)} + foci {fmtPts(p.echogenicFoci)} ={" "}
        <strong data-testid={`tirads-total-${idx}`}>{result.complete ? result.total : "–"}</strong>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
        <span>
          <strong>ACR TI-RADS:</strong>{" "}
          <span data-testid={`tirads-level-${idx}`}>{result.level ? `${result.level} (${result.label})` : "Incomplete"}</span>
        </span>
        <span>
          <strong>Max size:</strong> {result.maxDimensionCm !== null ? `${result.maxDimensionCm} cm` : "–"}
        </span>
        <span>
          <strong>Recommendation:</strong> <span data-testid={`tirads-rec-${idx}`}>{result.recommendation.text || "–"}</span>
        </span>
      </div>
    </div>
  );
}

function NoduleCard({ nodule, idx, onChange, onRemove }) {
  const set = (field, value) => onChange({ ...nodule, [field]: value });
  const result = computeTirads(nodule);
  const spongiform = nodule.composition === "spongiform";
  const conflicts = spongiformConflicts(nodule);
  return (
    <div className="border rounded-md p-3 mb-3" data-testid={`nodule-${idx}`}>
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-semibold">Nodule {idx + 1}</h4>
        <button type="button" onClick={onRemove} className="min-h-[40px] px-2 text-sm text-red-600 hover:text-red-800">
          Remove
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-2 items-end">
        <div>
          <label className="block text-sm font-medium mb-1">Location</label>
          <select
            className={inputCls}
            aria-label={`Nodule ${idx + 1} location`}
            value={nodule.lobe}
            onChange={(e) => onChange({ ...nodule, lobe: e.target.value, pole: e.target.value === "isthmus" ? "" : nodule.pole })}
          >
            <option value="">Select</option>
            {LOBES.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Pole</label>
          <select
            className={inputCls}
            aria-label={`Nodule ${idx + 1} pole`}
            value={nodule.pole}
            disabled={nodule.lobe === "isthmus"}
            onChange={(e) => set("pole", e.target.value)}
          >
            <option value="">Select</option>
            {POLES.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        {["L", "W", "H"].map((d) => (
          <div key={d}>
            <label className="block text-sm font-medium mb-1">{d} (cm)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              aria-label={`Nodule ${idx + 1} ${d} cm`}
              className={inputCls}
              value={nodule[d]}
              onChange={(e) => set(d, e.target.value)}
            />
          </div>
        ))}
      </div>

      <ChoiceRow label="Composition" options={COMPOSITION} value={nodule.composition} onPick={(v) => onChange({ ...nodule, composition: v, spongiformConfirmed: false })} testid={`n${idx}-composition`} />
      {spongiform && (
        <div className="mt-2 text-xs text-gray-600">Spongiform: no points are added for the other categories (ACR).</div>
      )}
      {conflicts.length > 0 && !nodule.spongiformConfirmed && (
        <div className="mt-2 rounded border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="alert" data-testid={`spongiform-warning-${idx}`}>
          Spongiform nodules rarely show {conflicts.join(", ")}. Please confirm the composition.
          <button
            type="button"
            onClick={() => set("spongiformConfirmed", true)}
            className="ml-2 min-h-[40px] px-3 py-1 border border-amber-500 rounded bg-white text-amber-900 hover:bg-amber-100"
          >
            Spongiform confirmed
          </button>
        </div>
      )}
      <ChoiceRow label="Echogenicity" options={ECHOGENICITY} value={nodule.echogenicity} onPick={(v) => set("echogenicity", v)} testid={`n${idx}-echogenicity`} />
      <ChoiceRow label="Shape" options={SHAPE} value={nodule.shape} onPick={(v) => set("shape", v)} testid={`n${idx}-shape`} />
      <ChoiceRow label="Margin" options={MARGIN} value={nodule.margin} onPick={(v) => set("margin", v)} testid={`n${idx}-margin`} />
      <ChoiceRow
        label="Echogenic foci (select all that apply)"
        options={FOCI}
        value={nodule.echogenicFoci}
        multi
        onPick={(v) => set("echogenicFoci", toggleFocus(nodule.echogenicFoci, v))}
        testid={`n${idx}-foci`}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
        <div>
          <label className="block text-sm font-medium mb-1">Vascularity (optional)</label>
          <select className={inputCls} aria-label={`Nodule ${idx + 1} vascularity`} value={nodule.vascularity} onChange={(e) => set("vascularity", e.target.value)}>
            <option value="">Not assessed</option>
            {VASCULARITY.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">Notes (optional)</label>
          <input className={inputCls} aria-label={`Nodule ${idx + 1} notes`} value={nodule.notes} onChange={(e) => set("notes", e.target.value)} />
        </div>
      </div>

      <TiradsResult result={result} idx={idx} />
    </div>
  );
}

function LymphNodeCard({ node, idx, onChange, onRemove }) {
  const set = (field, value) => onChange({ ...node, [field]: value });
  return (
    <div className="border rounded-md p-3 mb-3" data-testid={`lymphnode-${idx}`}>
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-semibold">Lymph node {idx + 1}</h4>
        <button type="button" onClick={onRemove} className="min-h-[40px] px-2 text-sm text-red-600 hover:text-red-800">
          Remove
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2 items-end">
        <div>
          <label className="block text-sm font-medium mb-1">Side</label>
          <select className={inputCls} aria-label={`Lymph node ${idx + 1} side`} value={node.side} onChange={(e) => set("side", e.target.value)}>
            <option value="">Select</option>
            {LN_SIDES.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Level</label>
          <select className={inputCls} aria-label={`Lymph node ${idx + 1} level`} value={node.level} onChange={(e) => set("level", e.target.value)}>
            <option value="">Select</option>
            {LN_LEVELS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Long axis (cm)</label>
          <input type="number" step="0.01" min="0" inputMode="decimal" aria-label={`Lymph node ${idx + 1} long axis cm`} className={inputCls} value={node.long} onChange={(e) => set("long", e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Short axis (cm)</label>
          <input type="number" step="0.01" min="0" inputMode="decimal" aria-label={`Lymph node ${idx + 1} short axis cm`} className={inputCls} value={node.short} onChange={(e) => set("short", e.target.value)} />
        </div>
      </div>
      <ChoiceRow
        label="Features (select all that apply)"
        options={LN_FEATURES}
        value={node.features}
        multi
        showPoints={false}
        onPick={(v) => set("features", toggleValue(node.features, v))}
        testid={`ln${idx}-feature`}
      />
      <div className="mt-3">
        <label className="block text-sm font-medium mb-1">Notes (optional)</label>
        <input className={inputCls} aria-label={`Lymph node ${idx + 1} notes`} value={node.notes} onChange={(e) => set("notes", e.target.value)} />
      </div>
    </div>
  );
}

function StatusChoice({ label, value, options, onPick, testid }) {
  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label={label}>
      {options.map((o) => (
        <Choice key={o.value} selected={value === o.value} onClick={() => onPick(o.value)} testid={`${testid}-${o.value}`}>
          {o.label}
        </Choice>
      ))}
    </div>
  );
}

export function TiradsFormSection({ nodulesStatus, setNodulesStatus, nodules, setNodules, lymphNodes, setLymphNodes }) {
  const addNodule = () => {
    setNodulesStatus("present");
    setNodules([...nodules, emptyNodule()]);
  };
  const pickNoduleStatus = (v) => {
    if (v === "none" && nodulesStatus === "present" && nodules.some(hasEntries)) {
      if (!window.confirm("Discard the nodules entered on this worksheet?")) return;
      setNodules([]);
    }
    setNodulesStatus(v);
    if (v === "present" && nodules.length === 0) setNodules([emptyNodule()]);
  };
  const pickLnStatus = (v) => {
    if (v !== "abnormal" && lymphNodes.status === "abnormal" && lymphNodes.nodes.some(hasEntries)) {
      if (!window.confirm("Discard the abnormal lymph nodes entered on this worksheet?")) return;
      setLymphNodes({ status: v, nodes: [] });
      return;
    }
    const nodes = v === "abnormal" && lymphNodes.nodes.length === 0 ? [emptyLymphNode()] : lymphNodes.nodes;
    setLymphNodes({ ...lymphNodes, status: v, nodes });
  };

  return (
    <div className="mb-4" data-testid="tirads-section">
      {/* Nodules */}
      <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
        <label className="block text-sm font-medium">Nodules (ACR TI-RADS)</label>
        <StatusChoice
          label="Nodules"
          value={nodulesStatus}
          onPick={pickNoduleStatus}
          testid="nodules-status"
          options={[
            { value: "none", label: "None seen" },
            { value: "present", label: "Nodules present" },
          ]}
        />
      </div>
      {nodulesStatus === "present" && (
        <>
          {nodules.map((n, i) => (
            <NoduleCard
              key={i}
              idx={i}
              nodule={n}
              onChange={(next) => setNodules(nodules.map((x, j) => (j === i ? next : x)))}
              onRemove={() => setNodules(nodules.filter((_, j) => j !== i))}
            />
          ))}
          <button type="button" onClick={addNodule} className="min-h-[40px] flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium mb-2">
            <FaPlus /> Add nodule
          </button>
        </>
      )}

      {/* Cervical lymph nodes */}
      <div className="flex flex-wrap justify-between items-center gap-2 mt-4 mb-2">
        <label className="block text-sm font-medium">Cervical Lymph Nodes</label>
        <StatusChoice
          label="Cervical lymph nodes"
          value={lymphNodes.status}
          onPick={pickLnStatus}
          testid="ln-status"
          options={LN_STATUSES}
        />
      </div>
      {lymphNodes.status === "abnormal" && (
        <>
          {lymphNodes.nodes.map((n, i) => (
            <LymphNodeCard
              key={i}
              idx={i}
              node={n}
              onChange={(next) => setLymphNodes({ ...lymphNodes, nodes: lymphNodes.nodes.map((x, j) => (j === i ? next : x)) })}
              onRemove={() => setLymphNodes({ ...lymphNodes, nodes: lymphNodes.nodes.filter((_, j) => j !== i) })}
            />
          ))}
          <button
            type="button"
            onClick={() => setLymphNodes({ ...lymphNodes, nodes: [...lymphNodes.nodes, emptyLymphNode()] })}
            className="min-h-[40px] flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            <FaPlus /> Add lymph node
          </button>
        </>
      )}
    </div>
  );
}

/* ---------- read-only report (preview page / Athena image) ---------- */

const dims = (o) => ["L", "W", "H"].map((k) => o[k] || "–").join(" x ");
const fociText = (list) => (Array.isArray(list) ? list.map((v) => labelOf(FOCI, v)).filter(Boolean).join(", ") : "");

export function TiradsReport({ data }) {
  const hasNodules = data && (data.nodulesStatus || Array.isArray(data.nodules));
  const ln = data && data.lymphNodes && typeof data.lymphNodes === "object" ? data.lymphNodes : null;
  if (!hasNodules && !ln) return null;
  // tolerate hand-edited / partial rows: skip null or non-object entries
  const nodules = Array.isArray(data.nodules) ? data.nodules.filter((n) => n && typeof n === "object") : [];
  const lnNodes = ln && Array.isArray(ln.nodes) ? ln.nodes.filter((n) => n && typeof n === "object") : [];

  return (
    <div className="text-sm text-start w-full mb-6" data-testid="tirads-report">
      {hasNodules && (
        <div className="mb-3">
          <strong>Nodules (ACR TI-RADS):</strong>{" "}
          {data.nodulesStatus === "none" ? "None seen" : ""}
          {nodules.map((n, i) => {
            // the saved computation is what the sonographer saw; recompute only if absent
            const t = n.tirads && typeof n.tirads === "object" ? n.tirads : computeTirads(n);
            const p = t.points || {};
            return (
              <div key={i} className="border border-gray-300 rounded p-2 mt-2" data-testid={`report-nodule-${i}`}>
                <div className="flex flex-wrap justify-between gap-x-4">
                  <span>
                    <strong>Nodule {i + 1}:</strong> {noduleLocation(n) || "–"}; {dims(n)} cm
                  </span>
                  <span>
                    <strong>{t.level ? `${t.level} (${t.label})` : "TI-RADS incomplete"}</strong>
                    {t.complete ? `, ${t.total} pts` : ""}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-6 mt-1">
                  {n.composition === "spongiform" ? (
                    <div className="col-span-2">Composition: Spongiform — other features not scored (ACR)</div>
                  ) : (
                    <>
                      <div>Composition: {reportLabelOf(COMPOSITION, n.composition) || "–"} ({fmtPts(p.composition)})</div>
                      <div>Echogenicity: {reportLabelOf(ECHOGENICITY, n.echogenicity) || "–"} ({fmtPts(p.echogenicity)})</div>
                      <div>Shape: {reportLabelOf(SHAPE, n.shape) || "–"} ({fmtPts(p.shape)})</div>
                      <div>Margin: {reportLabelOf(MARGIN, n.margin) || "–"} ({fmtPts(p.margin)})</div>
                      <div className="col-span-2">Echogenic foci: {fociText(n.echogenicFoci) || "–"} ({fmtPts(p.echogenicFoci)})</div>
                    </>
                  )}
                  {n.vascularity && <div>Vascularity: {labelOf(VASCULARITY, n.vascularity)}</div>}
                  {n.notes && <div className="col-span-2">Notes: {n.notes}</div>}
                </div>
                {t.recommendation && t.recommendation.text && (
                  <div className="mt-1">
                    <strong>Recommendation (ACR 2017):</strong> {t.recommendation.text}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {ln && (
        <div>
          <strong>Cervical Lymph Nodes:</strong>{" "}
          {labelOf(LN_STATUSES, ln.status) || "–"}
          {ln.status === "abnormal" &&
            lnNodes.map((n, i) => (
              <div key={i} className="ml-4 mt-1" data-testid={`report-lymphnode-${i}`}>
                {[labelOf(LN_SIDES, n.side), n.level ? `level ${n.level}` : ""].filter(Boolean).join(" ") || "Node"}
                {n.long || n.short ? `; ${n.long || "–"} x ${n.short || "–"} cm (long x short)` : ""}
                {Array.isArray(n.features) && n.features.length
                  ? `; ${n.features.map((f) => labelOf(LN_FEATURES, f)).filter(Boolean).join(", ")}`
                  : ""}
                {n.notes ? `; ${n.notes}` : ""}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
